import { Component, Inject, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewInit, PLATFORM_ID, HostListener, ChangeDetectorRef } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { DocumentAttachmentService } from '../document-attachment-picker/document-attachment.service';
import { PatientDocumentRequestService } from '../../../core/services/patient-document-request.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { WhatsAppService } from '../../../core/services/whatsapp.service';
import { WhatsAppBroadcastService } from '../../../core/services/whatsapp-broadcast.service';
import { WhatsAppAudioPlaybackService } from '../../../core/services/whatsapp-audio-playback.service';
import { VoiceNoteRecordingService, ProcessedRecording } from '../../../core/services/voice-note-recording.service';
import { PatientService } from '../../../core/services/patient.service';
import { UserService } from '../../../core/services/user.service';
import { GroupService } from '../../../core/services/group.service';
import { ColleagueService } from '../../../core/services/colleague.service';
import { PatientNoteService } from '../../../core/services/patient-note.service';
import { PatientCommunicationService } from '../../../core/services/patient-communication.service';
import { PatientPrescriptionService } from '../../../core/services/patient-prescription.service';
import { MedicationService } from '../../../core/services/medication.service';
import { PatientFinancialService, BillingDocument } from '../../../core/services/patient-financial.service';
import { SignatureService, SignatureContext } from '../../../core/services/signature.service';
import { PdfDownloadService } from '../../../core/services/pdf-download.service';
import { BaseComponent } from '../../../core/components/base/base.component';
import { FeedbackService } from '../../../core/services/feedback.service';
import { TokenUsageService } from '../../../core/services/token-usage.service';
import { ScreenWakeService } from '../../../core/services/screen-wake.service';
import { MatDialog } from '@angular/material/dialog';
import { takeUntil, debounceTime, distinctUntilChanged, Subject, combineLatest, firstValueFrom, Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { FormControl } from '@angular/forms';
import * as moment from 'moment';
import * as _ from 'lodash';
import { FileObject } from '../../../core/interfaces';
import { MediaSelectorDialogComponent } from '../media-selector-dialog/media-selector-dialog.component';
import { ImageDialogComponent } from '../image-dialog/image-dialog.component';
import { SA_LANDLINE_AREA_CODES } from '../../../core/data/app.data';

export interface WhatsAppChatDialogData {
  patientId: string;
  patient?: any;
  initialMessage?: string;
  initialMedia?: any[]; // Array of media objects (FileObject format)
  phoneNumber?: string;
}

@Component({
  selector: 'app-whatsapp-chat-dialog',
  templateUrl: './whatsapp-chat-dialog.component.html',
  styleUrls: ['./whatsapp-chat-dialog.component.scss']
})
export class WhatsAppChatDialogComponent extends BaseComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('messagesContainer') messagesContainer?: ElementRef;
  @ViewChild('messageInput') messageInput?: ElementRef;

  patient: any;
  conversations: any[] = [];
  selectedConversation: any = null;
  messages: any[] = [];
  newMessage: string = '';
  sending: boolean = false;
  loading: boolean = false;
  loadingMore: boolean = false;
  hasMoreMessages: boolean = true;
  messagesOffset: number = 0;
  messagesLimit: number = 50;

  // Voice note recording (state managed by service, but kept for template binding)
  recording: boolean = false;
  recordingTime: Date = new Date(0);
  isTouchDevice: boolean = false; // Device supports touch
  isUsingTouchInteraction: boolean = false; // Currently using touch interaction (for hybrid devices)
  pendingRecording: ProcessedRecording | null = null; // Store recording after completion, before sending
  touchStartX: number = 0;
  touchStartY: number = 0;
  touchCurrentX: number = 0;
  touchCurrentY: number = 0;
  showCancelHint: boolean = false;
  showLockHint: boolean = false; // Changed from showDeleteHint - slide up locks recording
  recordingPaused: boolean = false;
  isCancelingRecording: boolean = false; // Flag to prevent callback from setting pendingRecording when canceling
  shouldSendDirectly: boolean = false; // Flag to send recording directly without storing as pending (for click-to-record mode)
  pausedRecordingTime: Date = new Date(0); // Time when recording was paused
  private recordingTimerInterval: any = null; // Timer interval for updating recording time

  // Clipboard paste
  isInputFocused: boolean = false;
  private pasteHandler?: (event: ClipboardEvent) => void;

  // Media attachment
  attachedMedia: any[] = [];
  isDragOver: boolean = false;
  patientMedia: any[] = [];
  attachmentMenuOpen: boolean = false;

  // Prescription attachment
  patientPrescriptions: any[] = [];
  signatureContext!: SignatureContext;
  includeSignatureOnPrescription: boolean = false;
  selectedDoctor: any = {};

  // Billing document attachment
  billingDocuments: BillingDocument[] = [];

  // Send menu
  sendMenuOpen: boolean = false;
  includeSignatureInPdf: boolean = false;

  // Broadcast mode
  broadcastMode: boolean = false;
  selectedConversationsForBroadcast: Set<string> = new Set();
  private longPressTimer: any = null;
  private longPressDelay: number = 500; // 500ms for long press

  // Track if touch event just occurred to prevent double-triggering with click
  private touchEventJustOccurred: boolean = false;

  // Sidebar visibility
  sidebarExpanded: boolean = true;
  isMobile: boolean = false;

  // Colleague search
  colleagueSearchControl = new FormControl('');
  filteredColleagues$: any;
  colleaguesWithMobile: any[] = [];

  // Text message credits
  textMessageBalance: number | null = null;
  readonly WHATSAPP_MESSAGE_COST: number = 0.25; // Cost per WhatsApp message in credits

  // Computed properties (to avoid function calls in template)
  messagePlaceholder: string = 'Type a message...';

  // Audio playback is now fully managed by WhatsAppAudioPlaybackService

  isPremium: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<WhatsAppChatDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: WhatsAppChatDialogData,
    @Inject(PLATFORM_ID) private platformId: object,
    private whatsappService: WhatsAppService,
    private broadcastService: WhatsAppBroadcastService,
    private audioPlaybackService: WhatsAppAudioPlaybackService,
    private recordingService: VoiceNoteRecordingService,
    private patientService: PatientService,
    private userService: UserService,
    private groupService: GroupService,
    private colleagueService: ColleagueService,
    private patientNoteService: PatientNoteService,
    private patientCommunicationService: PatientCommunicationService,
    private patientPrescriptionService: PatientPrescriptionService,
    private medicationService: MedicationService,
    private patientFinancialService: PatientFinancialService,
    private signatureService: SignatureService,
    private pdfDownloadService: PdfDownloadService,
    private documentAttachmentService: DocumentAttachmentService,
    private patientDocumentRequestService: PatientDocumentRequestService,
    private usageService: TokenUsageService,
    private screenWake: ScreenWakeService,
    public override feedbackService: FeedbackService,
    public override dialog: MatDialog,
    private cdr: ChangeDetectorRef
  ) {
    super(feedbackService, dialog);

    // RecordRTC will be loaded dynamically when needed (like ai-dialog)

    // Subscribe to patient changes
    this.patientService.currentPatient$
      .pipe(takeUntil(this.destroy$))
      .subscribe(patient => {
        if (patient) {
          this.patient = patient;
          // Messages are automatically loaded by the service when patient changes
        }
      });

    this.groupService.selectedGroup$
      .pipe(takeUntil(this.destroy$))
      .subscribe((group: any) => {
        this.isPremium = group?.isPremium === true;
      })

    // Subscribe to conversations (automatically updated when patient changes)
    // Also include colleagues$ to re-merge when colleagues are loaded
    combineLatest([
      this.whatsappService.conversations$,
      this.patientService.currentPatient$,
      this.colleagueService.colleagues$.pipe(startWith([])) // Include colleagues to trigger re-merge when loaded
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([conversations, patient, colleagues]) => {
        // Update colleaguesWithMobile from the colleagues parameter to ensure it's current
        if (colleagues && Array.isArray(colleagues)) {
          this.colleaguesWithMobile = colleagues.filter((colleague: any) => {
            const contactNumber = colleague.contactNumber;
            return contactNumber && contactNumber.trim().length > 0;
          });
        } else {
          this.colleaguesWithMobile = [];
        }
        
        // Merge WhatsApp conversations with patient mobile numbers
        this.conversations = this.mergeConversationsWithPatientNumbers(conversations, patient);
        // If we have a specific phone number, select that conversation
        if (this.data.phoneNumber && !this.selectedConversation) {
          const conv = this.conversations.find(c => c.phoneNumber === this.data.phoneNumber);
          if (conv) {
            this.selectConversation(conv);
          }
        }
        // If no conversation selected and we have conversations, select the first one
        if (!this.selectedConversation && this.conversations.length > 0) {
          this.selectConversation(this.conversations[0]);
        }
        this.updateMessagePlaceholder();
      });

    // Subscribe to messages (automatically updated when patient changes or socket events occur)
    this.whatsappService.messages$
      .pipe(takeUntil(this.destroy$))
      .subscribe(allMessages => {
        // Update messages for the selected conversation
        if (this.selectedConversation) {
          const conversationMessages = allMessages
            .filter((msg: any) => {
              // Filter by phone number
              const phoneNumber = msg.direction === 'inbound' ? msg.from : msg.to;
              const phoneMatches = phoneNumber === this.selectedConversation.phoneNumber;
              
              // Also filter by patientId to ensure we only show messages linked to the current patient
              // This is especially important for colleague conversations where the same colleague
              // might have messages with different patients
              const patientMatches = !this.patient?.id || msg.patientId === this.patient.id;
              
              return phoneMatches && patientMatches;
            })
            .sort((a: any, b: any) => {
              // Sort messages chronologically by createdAt (oldest first)
              const timeA = new Date(a.createdAt).getTime();
              const timeB = new Date(b.createdAt).getTime();
              return timeA - timeB; // Oldest first
            });

          // Enhance messages with computed properties for template
          this.enhanceMessages(conversationMessages);
          this.messages = conversationMessages;
        } else {
          // If no conversation selected, filter by patientId only
          const filteredMessages = this.patient?.id 
            ? allMessages.filter((msg: any) => msg.patientId === this.patient.id)
            : allMessages;
          
          const sortedMessages = filteredMessages.sort((a: any, b: any) => {
            // Sort messages chronologically by createdAt (oldest first)
            const timeA = new Date(a.createdAt).getTime();
            const timeB = new Date(b.createdAt).getTime();
            return timeA - timeB; // Oldest first
          });

          // Enhance messages with computed properties for template
          this.enhanceMessages(sortedMessages);
          this.messages = sortedMessages;
        }
        this.scrollToBottom();
      });

    // Subscribe to text message credit balance (already loaded by TokenUsageService when group changes)
    this.usageService.textMessageCreditBalance$
      .pipe(takeUntil(this.destroy$))
      .subscribe((balance: number | null) => {
        this.textMessageBalance = balance;
        this.updateMessagePlaceholder();
        this.cdr.markForCheck();
      });

    // Initialize placeholder
    this.updateMessagePlaceholder();
  }

  get hasInsufficientCredits(): boolean {
    // Premium users have unlimited credits
    if (this.isPremium) {
      return false;
    }
    return this.textMessageBalance !== null && this.textMessageBalance < this.WHATSAPP_MESSAGE_COST;
  }

  get hasLowCredits(): boolean {
    // Premium users have unlimited credits
    if (this.isPremium) {
      return false;
    }
    return this.textMessageBalance !== null && this.textMessageBalance > 0 && this.textMessageBalance <= 5;
  }

  ngOnInit(): void {
    // Detect if device supports touch
    this.isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    // Detect mobile and set sidebar state
    if (isPlatformBrowser(this.platformId)) {
      this.isMobile = window.innerWidth < 768; // Mobile breakpoint
      this.sidebarExpanded = !this.isMobile; // Collapse by default on mobile
    }

    // Set initial message if provided
    if (this.data.initialMessage) {
      this.newMessage = this.data.initialMessage;
    }

    // Initialize colleague search
    this.initializeColleagueSearch();

    // Set initial media if provided
    if (this.data.initialMedia && this.data.initialMedia.length > 0) {

      // Normalize media objects to match the format expected by sendMedia
      this.attachedMedia = this.data.initialMedia.map((media: any) => {
        // Timeline media objects have: mediaUrl, mediaType (MIME type), mediaFileName
        // We need: mediaUrl, mimeType, filename, mediaType (WhatsApp type: image/document/video)
        const normalized = {
          mediaUrl: media.mediaUrl,
          mimeType: media.mediaType || media.mimeType || media.type, // MIME type (e.g., 'image/png')
          filename: media.mediaFileName || media.filename || 'media',
          mediaType: this.getMediaTypeFromMimeType(media.mediaType || media.mimeType || media.type), // WhatsApp type (image/document/video)
          preview: media.mediaUrl,
          isExisting: true
        };

        return normalized;
      }).filter((media: any) => media.mediaType !== null); // Filter out unsupported types

    }

    // Handle patient loading
    if (this.data.patient) {
      this.patient = this.data.patient;


      // Ensure patient is set in patientService so WhatsApp service loads messages and financial service loads documents
      const currentPatient = this.patientService.patient;
      if (!currentPatient || currentPatient.id !== this.data.patient.id) {

        this.patientService.setCurrentPatient(this.data.patient);
        // This will trigger PatientFinancialService to load billing documents automatically
      }
      this.updateMessagePlaceholder();
    } else if (this.data.patientId) {

      // Get patient from service or load it
      const currentPatient = this.patientService.patient;
      if (currentPatient && currentPatient.id === this.data.patientId) {
        this.patient = currentPatient;
        // Messages are automatically loaded by the service when patient changes
      } else {
        // Load patient by ID - this will trigger the WhatsApp service to load messages
        this.patientService.getPatientById(this.data.patientId)
          .pipe(takeUntil(this.destroy$))
          .subscribe(patient => {
            if (patient) {
              this.patient = patient;
              // Messages will be loaded automatically by WhatsApp service when patient is set
              this.updateMessagePlaceholder();
            }
          });
      }
    }


    // Subscribe to patient media (for selecting existing media)
    combineLatest([
      this.patientNoteService.patientMedia$.pipe(map(noteMedia => noteMedia ?? [])),
      this.patientCommunicationService.patientMedia$.pipe(map(communicationMedia => communicationMedia ?? []))
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([noteMedia, communicationMedia]) => {
        const combined = _.orderBy([...noteMedia, ...communicationMedia], [(media) => moment(media.createdAt), 'displayIndex'], ['asc', 'desc']);
        this.patientMedia = [...combined];
      });

    // Subscribe to patient prescriptions
    this.patientPrescriptionService.patientPrescriptions$
      .pipe(takeUntil(this.destroy$))
      .subscribe(prescriptions => {
        // Prescriptions come as a flat array from the service
        // The prescription-list component groups them locally, but we need the flat array
        if (prescriptions && Array.isArray(prescriptions)) {
          this.patientPrescriptions = _.orderBy(prescriptions, [
            (p: any) => p.date ? moment(p.date, 'DD/MM/YYYY').format('YYYY-MM-DD') : moment(p.createdAt).format('YYYY-MM-DD'),
            'createdAt'
          ], ['desc', 'desc']);
        } else {
          this.patientPrescriptions = [];
        }
      });

    // Subscribe to billing documents (invoices, receipts, statements)
    this.patientFinancialService.billingDocuments$
      .pipe(takeUntil(this.destroy$))
      .subscribe(documents => {
        // Log each document in detail
        /* if (documents && Array.isArray(documents)) {
          documents.forEach((doc, index) => {
            console.log(`[DIALOG] Billing document ${index}`, {
              id: doc?.id,
              documentType: doc?.documentType,
              pdfUrl: doc?.pdfUrl,
              pdfFileName: doc?.pdfFileName,
              hasPdfUrl: !!doc?.pdfUrl,
              allKeys: doc ? Object.keys(doc) : [],
              fullDocument: doc
            });
          });
        } */

        // Filter only documents that have PDFs available (invoices, receipts, estimates)
        // Statements might need to be generated on demand
        // PDFs can be in pdfUrl or in media array (mediaType === "application/pdf")
        if (documents && Array.isArray(documents)) {
          const filtered = documents.filter(doc => {
            if (!doc || (doc.documentType !== 'invoice' && doc.documentType !== 'receipt' && doc.documentType !== 'estimate')) {
              return false;
            }

            // Check for pdfUrl at root level
            if (doc.pdfUrl) {
              return true;
            }

            // Check for PDF in media array
            if (doc.media && Array.isArray(doc.media)) {
              const pdfMedia = doc.media.find((m: any) => m.mediaType === 'application/pdf' && m.mediaUrl);
              if (pdfMedia) {
                return true;
              }
            }

            return false;
          });

          // Map filtered documents to include pdfUrl from media if not at root
          const mappedDocuments = filtered.map(doc => {
            // If pdfUrl exists at root, use it
            if (doc.pdfUrl) {
              return doc;
            }

            // Otherwise, get PDF from media array
            if (doc.media && Array.isArray(doc.media)) {
              const pdfMedia = doc.media.find((m: any) => m.mediaType === 'application/pdf' && m.mediaUrl);
              if (pdfMedia) {
                return {
                  ...doc,
                  pdfUrl: pdfMedia.mediaUrl,
                  pdfFileName: pdfMedia.mediaFileName || doc.pdfFileName
                };
              }
            }

            return doc;
          });

          this.billingDocuments = _.orderBy(
            mappedDocuments,
            ['issueDate', 'createdAt'],
            ['desc', 'desc']
          );
         
        } else {
          this.billingDocuments = [];
        }
      });

    // Load signature context
    this.loadSignatureContext();
  }

  private loadSignatureContext(): void {
    // Get initial signature context
    this.signatureContext = this.signatureService.getSignatureContext();

    // Subscribe to user profile changes to update signature context
    this.userService.userProfile$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.signatureContext = this.signatureService.getSignatureContext();
      });

    // Subscribe to group users changes to update signature context
    this.groupService.groupUsers$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.signatureContext = this.signatureService.getSignatureContext();
      });
  }

  ngAfterViewInit(): void {
    // Initialize textarea height
    setTimeout(() => {
      this.autoResizeTextarea();

      // Focus on message input if we have an initial message
      if (this.data.initialMessage && this.messageInput) {
        this.messageInput?.nativeElement.focus();
        this.autoResizeTextarea(); // Resize again after setting initial message
      }

      // Set up paste event listener
      this.setupPasteListener();
    }, 100);
  }

  autoResizeTextarea(): void {
    if (!this.messageInput) {
      return;
    }

    const textarea = this.messageInput.nativeElement;
    if (!textarea) {
      return;
    }

    // Check if textarea is empty or only has whitespace
    const hasContent = this.newMessage && this.newMessage.trim().length > 0;

    if (!hasContent) {
      // Empty state - set to minimum height and hide overflow
      textarea.style.height = '24px';
      textarea.style.overflowY = 'hidden';

      // Update container alignment
      const container = textarea.closest('.input-container');
      if (container) {
        container.style.alignItems = 'center';
      }
      return;
    }

    // Reset height to get accurate scrollHeight
    textarea.style.height = 'auto';
    textarea.style.overflowY = 'hidden';

    // Calculate new height (min 24px, max 120px)
    const scrollHeight = textarea.scrollHeight;
    const newHeight = Math.min(Math.max(24, scrollHeight), 120);
    textarea.style.height = `${newHeight}px`;

    // Only show scrollbar if content exceeds max-height
    if (scrollHeight > 120) {
      textarea.style.overflowY = 'auto';
    } else {
      textarea.style.overflowY = 'hidden';
    }

    // Update container alignment if needed
    const container = textarea.closest('.input-container');
    if (container) {
      if (newHeight > 24) {
        container.style.alignItems = 'flex-end';
      } else {
        container.style.alignItems = 'center';
      }
    }
  }

  // Note: Messages are now automatically loaded by the WhatsAppService when patient changes
  // This method is kept for backward compatibility but is no longer needed

  /**
   * Merge WhatsApp conversations with patient mobile numbers from the patient record
   * This includes patient's own numbers, liable party, emergency contacts, etc.
   */
  private mergeConversationsWithPatientNumbers(conversations: any[], patient: any): any[] {
    if (!patient) {
      return conversations || [];
    }

    // Extract all mobile numbers from patient record with their labels and names
    const patientNumbers: Array<{ phoneNumber: string; labels: string[]; personName?: string }> = [];

    // Helper function to normalize phone number (remove spaces, dashes, etc.)
    const normalizePhoneNumber = (phone: string | null | undefined): string | null => {
      if (!phone) return null;
      // Remove all non-digit characters except +
      const normalized = phone.replace(/[^\d+]/g, '');
      // Ensure it starts with + for international format
      return normalized.startsWith('+') ? normalized : `+${normalized}`;
    };

    // Helper function to format person name
    const formatPersonName = (title?: string, name?: string, surname?: string, initials?: string): string | undefined => {
      if (name && surname) {
        const titlePart = title ? `${title} ` : '';
        return `${titlePart}${name} ${surname}`.trim();
      } else if (name) {
        return name;
      } else if (initials && surname) {
        return `${initials} ${surname}`.trim();
      }
      return undefined;
    };

    // Helper function to check if a phone number is a South African landline
    const isLandline = (normalizedPhone: string): boolean => {
      // Check if number starts with +27 (South Africa country code)
      // +27 converts to 0, so +2711 becomes 011
      if (normalizedPhone.startsWith('+27')) {
        // Extract the 2-digit area code after +27 (positions 3-4)
        const areaCodeDigits = normalizedPhone.substring(3, 5);
        // Prepend 0 to convert +27 to 0 format (e.g., +2711 -> 011)
        const areaCode = `0${areaCodeDigits}`;
        return SA_LANDLINE_AREA_CODES.includes(areaCode);
      }
      // Check if number starts with +0 (local format with leading 0)
      if (normalizedPhone.startsWith('+0')) {
        // Extract the area code (3 digits after +, positions 1-3)
        const areaCode = normalizedPhone.substring(1, 4);
        return SA_LANDLINE_AREA_CODES.includes(areaCode);
      }
      // If number doesn't match expected formats, don't exclude it
      return false;
    };

    // Helper function to add number with label and name
    const addNumber = (phone: string | null | undefined, label: string, personName?: string) => {
      const normalized = normalizePhoneNumber(phone);
      if (normalized) {
        // Exclude landline numbers
        if (isLandline(normalized)) {
          return;
        }
        const existing = patientNumbers.find(p => p.phoneNumber === normalized);
        if (existing) {
          if (!existing.labels.includes(label)) {
            existing.labels.push(label);
          }
          // Update name if provided and not already set
          if (personName && !existing.personName) {
            existing.personName = personName;
          }
        } else {
          patientNumbers.push({ phoneNumber: normalized, labels: [label], personName });
        }
      }
    };

    // Patient's own mobile number
    const patientName = formatPersonName(patient.title, patient.name, patient.surname, patient.initials);
    addNumber(patient.mobileNumber, 'Patient', patientName);

    // Patient's phone number (if different from mobile)
    addNumber(patient.phoneNumber, 'Patient', patientName);

    // Liable party (person responsible for account)
    const liableName = formatPersonName(patient.liableTitle, patient.liableName, patient.liableSurname, patient.liableInitials);
    addNumber(patient.liableMobileNumber, 'Account Responsible', liableName);
    addNumber(patient.liablePhoneNumber, 'Account Responsible', liableName);

    // Emergency contact 1
    if (patient.emergencyContactMobile || patient.emergencyContactPhone) {
      const relationship = patient.emergencyContactRelationship || 'Emergency Contact';
      const emergencyContactName = patient.emergencyContact || undefined;
      addNumber(patient.emergencyContactMobile, relationship, emergencyContactName);
      addNumber(patient.emergencyContactPhone, relationship, emergencyContactName);
    }

    // Emergency contact 2
    if (patient.emergencyContactMobile2 || patient.emergencyContactPhone2) {
      const relationship = patient.emergencyContactRelationship2 || 'Emergency Contact 2';
      const emergencyContact2Name = patient.emergencyContact2 || undefined;
      addNumber(patient.emergencyContactMobile2, relationship, emergencyContact2Name);
      addNumber(patient.emergencyContactPhone2, relationship, emergencyContact2Name);
    }

    // Work phone (if mobile-like) - no specific name field for work contact
    addNumber(patient.workPhone, 'Work');

    // Create a map of existing conversations by phone number
    const conversationMap = new Map<string, any>();
    (conversations || []).forEach(conv => {
      const normalized = normalizePhoneNumber(conv.phoneNumber);
      if (normalized) {
        conversationMap.set(normalized, conv);
      }
    });

    // Merge patient numbers with existing conversations
    const merged: any[] = [];

    // Helper to normalize phone for comparison (same as normalizePhoneNumber but for comparison)
    const normalizeForComparison = (phone: string): string => {
      if (!phone) return '';
      return phone.replace(/[^\d+]/g, '').toLowerCase();
    };
    
    // Store reference for logging
    const normalizePhoneNumberForComparison = (phone: string | null | undefined): string | null => {
      if (!phone) return null;
      const normalized = phone.replace(/[^\d+]/g, '');
      return normalized.startsWith('+') ? normalized : `+${normalized}`;
    };

    // First, add all existing conversations (they have message history)
    conversationMap.forEach((conv, phoneNumber) => {
      // Check if this number is also in patient numbers
      const patientNumber = patientNumbers.find(p => normalizeForComparison(p.phoneNumber) === normalizeForComparison(phoneNumber));
      if (patientNumber) {
        // Merge labels from patient record
        conv.relationshipLabels = [...new Set([...(conv.relationshipLabels || []), ...patientNumber.labels])];
        // Set person name if available and not already set
        if (patientNumber.personName && !conv.personName) {
          conv.personName = patientNumber.personName;
        }
      } else {
        // Check if this conversation is with a colleague
        const colleague = this.colleaguesWithMobile.find((c: any) => {
          const colleaguePhone = normalizePhoneNumber(c.contactNumber);
          const colleagueNormalized = colleaguePhone ? normalizeForComparison(colleaguePhone) : '';
          const conversationNormalized = normalizeForComparison(phoneNumber);
          return colleaguePhone && colleagueNormalized === conversationNormalized;
        });
        
        if (colleague) {
          // This is a colleague conversation - add colleague metadata
          if (!conv.relationshipLabels || !conv.relationshipLabels.includes('Colleague')) {
            conv.relationshipLabels = [...new Set([...(conv.relationshipLabels || []), 'Colleague'])];
          }
          // Set person name if not already set
          if (!conv.personName) {
            const colleagueName = formatPersonName(colleague.title, colleague.name, colleague.surname);
            if (colleagueName) {
              conv.personName = colleagueName;
            }
          }
        }
      }
      merged.push(conv);
    });

    // Then, add patient numbers that don't have conversations yet
    patientNumbers.forEach(patientNumber => {
      if (!conversationMap.has(patientNumber.phoneNumber)) {
        // Create a new conversation entry for this number
        merged.push({
          phoneNumber: patientNumber.phoneNumber,
          messages: [],
          lastMessageAt: null,
          lastMessage: null,
          unreadCount: 0,
          hasPendingMessage: false,
          isEmpty: true,
          relationshipLabels: patientNumber.labels,
          personName: patientNumber.personName,
          isCurrentNumber: patientNumber.phoneNumber === normalizePhoneNumber(patient.mobileNumber),
          isHistorical: false
        });
      }
    });

    // Sort: conversations with messages first, then by relationship priority
    const relationshipPriority: { [key: string]: number } = {
      'Patient': 1,
      'Account Responsible': 2,
      'Emergency Contact': 3,
      'Emergency Contact 2': 4,
      'Work': 5,
      'Colleague': 6
    };

    merged.sort((a, b) => {
      // First, sort by whether they have messages (conversations with messages come first)
      const aHasMessages = a.lastMessage !== null || (a.messages && a.messages.length > 0);
      const bHasMessages = b.lastMessage !== null || (b.messages && b.messages.length > 0);

      if (aHasMessages && !bHasMessages) return -1;
      if (!aHasMessages && bHasMessages) return 1;

      // If both have messages or both don't, sort by relationship priority
      const aLabels = a.relationshipLabels || [];
      const bLabels = b.relationshipLabels || [];

      const aPriority = aLabels.length > 0 ? Math.min(...aLabels.map((l: string) => relationshipPriority[l] || 999)) : 999;
      const bPriority = bLabels.length > 0 ? Math.min(...bLabels.map((l: string) => relationshipPriority[l] || 999)) : 999;

      if (aPriority !== bPriority) {
        return aPriority - bPriority;
      }

      // If same priority, sort by last message time (most recent first)
      if (a.lastMessageAt && b.lastMessageAt) {
        return new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime();
      }

      return 0;
    });

    return merged;
  }

  /**
   * Initialize colleague search functionality
   */
  private initializeColleagueSearch(): void {
    // Subscribe to colleagues and filter those with mobile numbers
    this.colleagueService.colleagues$
      .pipe(takeUntil(this.destroy$))
      .subscribe(colleagues => {
        if (colleagues && Array.isArray(colleagues)) {
          // Filter colleagues who have a contact number (mobile number)
          this.colleaguesWithMobile = colleagues.filter((colleague: any) => {
            const contactNumber = colleague.contactNumber;
            return contactNumber && contactNumber.trim().length > 0;
          });
        } else {
          this.colleaguesWithMobile = [];
        }
      });

    // Setup filtering for search input
    this.filteredColleagues$ = this.colleagueSearchControl.valueChanges.pipe(
      startWith(''),
      map(value => {
        // Handle case where value might be an object (when colleague is selected) or non-string
        const searchTerm = typeof value === 'string' ? value : '';
        return this.filterColleagues(searchTerm);
      })
    );
  }

  /**
   * Filter colleagues by search term
   */
  private filterColleagues(searchTerm: string): any[] {
    // Ensure searchTerm is a string
    if (!searchTerm || typeof searchTerm !== 'string' || searchTerm.trim() === '') {
      return [];
    }

    const term = searchTerm.toLowerCase().trim();
    return this.colleaguesWithMobile.filter((colleague: any) => {
      const name = `${colleague.title || ''} ${colleague.name || ''} ${colleague.surname || ''}`.toLowerCase();
      const speciality = (colleague.speciality || '').toLowerCase();
      const contactNumber = (colleague.contactNumber || '').toLowerCase();

      return name.includes(term) ||
        speciality.includes(term) ||
        contactNumber.includes(term);
    });
  }

  /**
   * Display function for colleague autocomplete
   */
  displayColleagueFn(colleague: any): string {
    if (!colleague) return '';
    const name = `${colleague.title || ''} ${colleague.name || ''} ${colleague.surname || ''}`.trim();
    const speciality = colleague.speciality ? ` (${colleague.speciality})` : '';
    return `${name}${speciality}`;
  }

  /**
   * Handle colleague selection from search
   */
  onColleagueSelected(colleague: any): void {
    if (!colleague || !colleague.contactNumber) {
      return;
    }

    // Normalize phone number (ensure it starts with +)
    const normalizePhoneNumber = (phone: string): string => {
      const normalized = phone.replace(/[^\d+]/g, '');
      return normalized.startsWith('+') ? normalized : `+${normalized}`;
    };

    const phoneNumber = normalizePhoneNumber(colleague.contactNumber);

    // Check if conversation already exists
    const existingConversation = this.conversations.find(c => c.phoneNumber === phoneNumber);

    if (existingConversation) {
      // Select existing conversation
      this.selectConversation(existingConversation);
    } else {
      // Create new conversation entry
      const newConversation = {
        phoneNumber,
        messages: [],
        lastMessageAt: null,
        lastMessage: null,
        unreadCount: 0,
        hasPendingMessage: false,
        isEmpty: true,
        relationshipLabels: ['Colleague'],
        personName: `${colleague.title || ''} ${colleague.name || ''} ${colleague.surname || ''}`.trim(),
        isCurrentNumber: false,
        isHistorical: false
      };

      // Add to conversations list
      this.conversations = [...this.conversations, newConversation];

      // Select the new conversation
      this.selectConversation(newConversation);
    }

    // Clear search
    this.colleagueSearchControl.setValue('');
  }

  handleColleagueSearchKeydown(event: KeyboardEvent): void {
    if (event.key === 'Tab') {
      event.stopPropagation();
    }
  }

  /**
   * Broadcast mode methods
   */
  enableBroadcastMode(conversation: any): void {
    this.broadcastMode = true;
    this.selectedConversationsForBroadcast.clear();
    // Auto-select the conversation that triggered broadcast mode
    this.selectedConversationsForBroadcast.add(conversation.phoneNumber);
    // Clear single conversation selection
    this.selectedConversation = null;
    this.messages = [];
  }

  exitBroadcastMode(): void {
    this.broadcastMode = false;
    this.selectedConversationsForBroadcast.clear();
    // Restore single conversation selection if available
    if (this.conversations.length > 0) {
      this.selectConversation(this.conversations[0]);
    } else {
      this.updateMessagePlaceholder();
    }
  }

  toggleConversationForBroadcast(conversation: any, checked: boolean | null): void {
    if (checked) {
      this.selectedConversationsForBroadcast.add(conversation.phoneNumber);
    } else {
      this.selectedConversationsForBroadcast.delete(conversation.phoneNumber);
    }
  }

  onConversationClick(conversation: any, event: MouseEvent): void {
    if (this.broadcastMode) {
      // In broadcast mode, clicking toggles selection
      event.preventDefault();
      event.stopPropagation();
      const isSelected = this.selectedConversationsForBroadcast.has(conversation.phoneNumber);
      this.toggleConversationForBroadcast(conversation, !isSelected);
    } else {
      // Normal mode: select conversation
      this.selectConversation(conversation);
    }
  }

  onConversationMouseDown(conversation: any, event: MouseEvent): void {
    if (this.broadcastMode) {
      return; // Don't handle long press in broadcast mode
    }

    // Start long press timer
    this.longPressTimer = setTimeout(() => {
      this.enableBroadcastMode(conversation);
      this.longPressTimer = null;
    }, this.longPressDelay);
  }

  onConversationMouseUp(event: MouseEvent): void {
    // Clear long press timer if mouse is released before delay
    if (this.longPressTimer) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }
  }

  onConversationMouseLeave(event: MouseEvent): void {
    // Clear long press timer if mouse leaves the element
    if (this.longPressTimer) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }
  }

  onConversationTouchStart(conversation: any, event: TouchEvent): void {
    if (this.broadcastMode) {
      return; // Don't handle long press in broadcast mode
    }

    // Start long press timer for touch
    this.longPressTimer = setTimeout(() => {
      this.enableBroadcastMode(conversation);
      this.longPressTimer = null;
      // Prevent default to avoid triggering click
      event.preventDefault();
    }, this.longPressDelay);
  }

  onConversationTouchEnd(event: TouchEvent): void {
    // Clear long press timer if touch ends before delay
    if (this.longPressTimer) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }
  }

  onConversationTouchCancel(event: TouchEvent): void {
    // Clear long press timer if touch is cancelled
    if (this.longPressTimer) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }
  }

  selectConversation(conversation: any): void {
    if (this.broadcastMode) {
      return; // Don't select single conversation in broadcast mode
    }

    this.selectedConversation = conversation;
    this.updateMessagePlaceholder();

    // Reset pagination state when selecting a new conversation
    this.messagesOffset = 0;
    this.hasMoreMessages = true;
    this.loadingMore = false;

    // Get all messages from the service
    const allMessages = this.whatsappService.getCurrentMessages() || [];

    // Filter and sort messages for this conversation
    const conversationMessages = allMessages
      .filter((msg: any) => {
        // Filter by phone number
        const phoneNumber = msg.direction === 'inbound' ? msg.from : msg.to;
        const phoneMatches = phoneNumber === conversation.phoneNumber;
        
        // Also filter by patientId to ensure we only show messages linked to the current patient
        // This is especially important for colleague conversations where the same colleague
        // might have messages with different patients
        const patientMatches = !this.patient?.id || msg.patientId === this.patient.id;
        
        return phoneMatches && patientMatches;
      })
      .sort((a: any, b: any) => {
        // Sort messages chronologically by createdAt (oldest first)
        const timeA = new Date(a.createdAt).getTime();
        const timeB = new Date(b.createdAt).getTime();
        return timeA - timeB; // Oldest first
      });

    // Enhance messages with computed properties for template
    this.enhanceMessages(conversationMessages);
    this.messages = conversationMessages;

    // Optionally load full conversation if needed (for pagination or more messages)
    // This is optional and can be done if you want to load more messages than what's in memory
    if (conversation.phoneNumber && conversationMessages.length === 0) {
      this.loading = true;
      this.whatsappService.loadConversation(conversation.phoneNumber)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            this.loading = false;
            if (response.success && response.data) {
              // Messages will be updated via the service's messages$ observable
              // so we don't need to set them directly here
            }
          },
          error: (error) => {
            this.loading = false;
            console.error('Error loading conversation:', error);
          }
        });
    } else {
      this.loading = false;
    }

    // Close sidebar on mobile when conversation is selected (unless in broadcast mode)
    if (this.isMobile && !this.broadcastMode) {
      this.sidebarExpanded = false;
    }

    this.scrollToBottom();
  }

  async sendMessage(): Promise<void> {
    if (!this.newMessage.trim()) {
      return;
    }

    // Handle conversation selection for single sends
    if (!this.broadcastMode) {
      let phoneNumber = this.selectedConversation?.phoneNumber;

      if (!phoneNumber && this.patient?.mobileNumber) {
        phoneNumber = this.patient.mobileNumber;

        // If patient mobile number is used, create/select conversation for it
        if (!this.selectedConversation) {
          const conversations = this.whatsappService.getCurrentConversations() || [];
          let conversation = conversations.find(c => c.phoneNumber === phoneNumber);
          if (!conversation) {
            conversation = {
              phoneNumber,
              messages: [],
              lastMessageAt: null,
              unreadCount: 0,
              hasPendingMessage: false
            };
          }
          this.selectConversation(conversation);
        }
      }
    }

    const messageToSend = this.newMessage.trim();

    await this.sendUnified(
      (phoneNumber, options) => {
        return this.whatsappService.sendTextMessage(phoneNumber, messageToSend, options);
      },
      'message',
      true,
      (response) => {
        if (response.waitingForButtonResponse) {
          this.showToastMessage('Message queued — waiting for recipient to confirm consent');
        } else if (response.introTemplateSent) {
          this.showToastMessage('Intro template and message sent successfully');
        } else {
          this.showToastMessage('Message sent successfully');
        }
        this.scrollToBottom();
      },
      (error, restoreValue) => {
        if (restoreValue !== undefined) {
          this.newMessage = restoreValue;
        }
        if (error?.errorCode === 'OUTSIDE_WINDOW') {
          this.showToastMessage('Cannot send free-form message outside 24-hour window. Enable auto-send intro template to send template first.');
        } else {
          this.showToastMessage(error?.error || 'Failed to send message');
        }
      },
      messageToSend,
      { messageType: 'text', content: messageToSend }
    );
  }

  formatPhoneNumber(phoneNumber: string): string {
    return this.whatsappService.formatPhoneNumber(phoneNumber);
  }

  // ==================== HELPER METHODS (Phase 1 Refactoring) ====================

  /**
   * Unified send method that handles both single and broadcast modes
   * @param sendFn - Function that sends to a single phone number (returns Observable)
   * @param messageType - Type of message for error/success messages
   * @param includeAutoSend - Whether to include autoSendIntroTemplate flag
   * @param onSuccess - Optional custom success handler
   * @param onError - Optional custom error handler
   * @param restoreOnFailure - Optional value to restore on failure (e.g., message text)
   */
  private async sendUnified(
    sendFn: (phoneNumber: string, options: any) => Observable<any>,
    messageType: string,
    includeAutoSend: boolean = true,
    onSuccess?: (response: any) => void,
    onError?: (error: any, restoreValue?: any) => void,
    restoreOnFailure?: any,
    optimisticPayload?: { messageType: 'text' | 'voice' | 'media'; content?: string; mediaType?: string; mediaFileName?: string }
  ): Promise<void> {
    // Handle broadcast mode
    if (this.broadcastMode) {
      if (!this.validateBroadcastMode()) {
        return;
      }

      const phoneNumbers = Array.from(this.selectedConversationsForBroadcast);

      // Clear state
      this.newMessage = '';
      this.autoResizeTextarea();
      this.sending = true;

      try {
        const result = await this.broadcastService.sendBroadcast(
          phoneNumbers,
          this.conversations,
          this.patient?.id,
          (conversation) => this.buildMessageOptions(conversation, includeAutoSend),
          sendFn,
          this.destroy$
        );

        this.sending = false;
        const messageText = this.broadcastService.formatResultMessage(result, messageType);
        this.showToastMessage(messageText);

        if (result.successCount === 0 && restoreOnFailure !== undefined) {
          if (onError) {
            onError(result, restoreOnFailure);
          } else {
            // Restore value on complete failure
            this.newMessage = restoreOnFailure;
          }
        }
      } catch (error) {
        this.sending = false;
        if (onError) {
          onError(error, restoreOnFailure);
        } else {
          this.handleSendError(error, restoreOnFailure, `sending broadcast ${messageType}`);
        }
      }
      return;
    }

    // Single send mode
    const phoneNumber = this.getPhoneNumberOrError();
    if (!phoneNumber) {
      return;
    }

    // Build message options
    const options = this.buildMessageOptions(this.selectedConversation, includeAutoSend);

    // Clear state
    this.newMessage = '';
    this.autoResizeTextarea();
    this.sending = true;

    // Send message
    sendFn(phoneNumber, options)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.sending = false;
          if (response.success) {
            // Centrally surface a pending row when the backend has queued
            // the message behind the consent template. Each caller's
            // onSuccess still fires for type-specific cleanup (e.g.
            // cleanupRecorder for voice notes). Without this, the user
            // would see the loader flash and then nothing in the chat.
            if (response.status === 'pending_button_response' && response.pendingMessageId) {
              this.whatsappService.pushOptimisticPending({
                id: response.pendingMessageId,
                templateMessageId: response.introTemplateMessageId,
                messageType: optimisticPayload?.messageType || 'text',
                to: phoneNumber,
                content: optimisticPayload?.content || null,
                mediaType: optimisticPayload?.mediaType || null,
                mediaFileName: optimisticPayload?.mediaFileName || null,
                patientId: this.patient?.id,
                groupId: this.whatsappService.groupId,
                status: 'pending',
                expiresAt: new Date(Date.now() + 60 * 60 * 1000).toISOString(),
                createdAt: new Date().toISOString(),
                retryable: true
              });
              this.scrollToBottom();
            } else if (response.messageId && optimisticPayload) {
              // Direct send (within 24h window). Push an optimistic row so
              // the UI updates immediately even if `whatsappMessageSent`
              // socket event is delayed or lost — the send already succeeded
              // server-side, the recipient will receive it, and waiting for
              // the round-trip is what produced the "loader flashes, nothing
              // appears" symptom on flaky web/mobile networks. Reconciles by
              // messageId when the socket event arrives.
              const responseType = response.type;
              const messageType = (responseType === 'text' || responseType === 'audio' || responseType === 'image' || responseType === 'video' || responseType === 'document')
                ? responseType
                : (optimisticPayload.messageType === 'voice' ? 'audio' : (optimisticPayload.messageType === 'media' ? (optimisticPayload.mediaType?.split('/')[0] || 'document') : 'text'));
              this.whatsappService.pushOptimisticDelivered({
                messageId: response.messageId,
                messageType,
                to: phoneNumber,
                content: optimisticPayload.content || null,
                mediaType: messageType === 'audio' ? 'audio/ogg' : (optimisticPayload.mediaType || null),
                mediaFileName: optimisticPayload.mediaFileName || null,
                patientId: this.patient?.id,
                groupId: this.whatsappService.groupId,
              });
              this.scrollToBottom();
            }
            if (onSuccess) {
              onSuccess(response);
            } else {
              this.showToastMessage(`${messageType} sent successfully`);
              this.scrollToBottom();
            }
          } else {
            if (onError) {
              onError(response, restoreOnFailure);
            } else {
              this.showToastMessage(response.error || `Failed to send ${messageType}`);
              if (restoreOnFailure !== undefined) {
                this.newMessage = restoreOnFailure;
              }
            }
          }
        },
        error: (error) => {
          this.sending = false;
          if (onError) {
            onError(error, restoreOnFailure);
          } else {
            this.handleSendError(error, restoreOnFailure, `sending ${messageType}`);
          }
        }
      });
  }

  /**
   * Builds WhatsApp message options with appropriate template based on conversation type
   * @param conversation - The conversation object (optional for single sends)
   * @param includeAutoSend - Whether to include autoSendIntroTemplate flag
   * @returns Message options object
   */
  private buildMessageOptions(conversation?: any, includeAutoSend: boolean = false): any {
    const options: any = {};

    if (includeAutoSend) {
      options.autoSendIntroTemplate = true;
    }

    // Always include patientId to link messages to the patient (required for conversations to appear)
    options.patientId = this.patient?.id;

    const isColleague = conversation?.relationshipLabels?.includes('Colleague');

    if (isColleague) {
      if (conversation?.personName) {
        options.colleagueName = conversation.personName;
      }
      options.introTemplateName = 'medicai_colleague_intro_notification_v2';
    } else {
      options.introTemplateName = 'medicai_patient_intro_notification_v2';
    }

    return options;
  }

  /**
   * Gets phone number from selected conversation or patient, or shows error
   * @returns Phone number string or null if not available
   */
  private getPhoneNumberOrError(): string | null {
    const phoneNumber = this.selectedConversation?.phoneNumber || this.patient?.mobileNumber;
    if (!phoneNumber) {
      this.showToastMessage('No phone number available. Please select a conversation or ensure the patient has a mobile number.');
      return null;
    }
    return phoneNumber;
  }

  /**
   * Validates that broadcast mode has at least one conversation selected
   * @returns true if valid, false otherwise (and shows error message)
   */
  private validateBroadcastMode(): boolean {
    if (this.selectedConversationsForBroadcast.size === 0) {
      this.showToastMessage('Please select at least one conversation to send a broadcast message.');
      return false;
    }
    return true;
  }

  /**
   * Formats broadcast result message based on success/failure counts
   * @param successCount - Number of successful sends
   * @param totalCount - Total number of recipients
   * @param messageType - Type of message (e.g., 'message', 'PDF', 'voice note')
   * @returns Formatted message string
   */
  private formatBroadcastResult(successCount: number, totalCount: number, messageType: string): string {
    if (successCount === totalCount) {
      return `Broadcast ${messageType} sent successfully to ${successCount} conversation${successCount > 1 ? 's' : ''}.`;
    } else if (successCount > 0) {
      const failureCount = totalCount - successCount;
      return `Broadcast ${messageType} sent to ${successCount} of ${totalCount} conversation${totalCount > 1 ? 's' : ''}. ${failureCount} failed.`;
    } else {
      return `Failed to send broadcast ${messageType} to all conversations.`;
    }
  }

  /**
   * Handles send errors with consistent error handling pattern
   * @param error - The error object
   * @param originalMessage - Optional message to restore on failure
   * @param context - Context string for logging (e.g., 'sending message', 'sending PDF')
   */
  private handleSendError(error: any, originalMessage?: string, context: string = 'send'): void {
    this.sending = false;
    if (originalMessage) {
      this.newMessage = originalMessage;
    }
    console.error(`[DIALOG] Error ${context}:`, error);
    this.showToastMessage(`Failed to ${context}. Please try again.`);
  }

  onImageError(event: Event): void {
    const target = event.target as HTMLImageElement;
    if (target) {
      target.style.display = 'none';
    }
  }

  openImageViewer(message: any): void {
    if (!message.mediaUrl || !message.mediaType?.startsWith('image/')) {
      return;
    }

    // Convert WhatsApp message to FileObject format
    const fileObject: FileObject = {
      url: message.mediaUrl,
      type: message.mediaType,
      originalPdfUrl: undefined
    };

    // Open image dialog (no editing for WhatsApp messages)
    this.dialog.open(ImageDialogComponent, {
      data: {
        fileObjects: [fileObject],
        selectedFile: fileObject,
        enableEditing: false
      },
      panelClass: 'image-modal-panel'
    });
  }

  formatRecordingTime(date: Date): string {
    const totalSeconds = Math.floor(date.getTime() / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  formatMessageTime(message: any): string {
    if (message.timestamp) {
      const date = new Date(message.timestamp * 1000);
      return moment(date).format('HH:mm');
    } else if (message.createdAt) {
      return moment(message.createdAt).format('HH:mm');
    }
    return '';
  }

  formatMessageDate(message: any): string {
    if (message.timestamp) {
      const date = new Date(message.timestamp * 1000);
      return moment(date).format('DD MMM YYYY');
    } else if (message.createdAt) {
      return moment(message.createdAt).format('DD MMM YYYY');
    }
    return '';
  }

  shouldShowDateSeparator(currentMessage: any, previousMessage: any): boolean {
    if (!previousMessage) return true;

    const currentDate = currentMessage.timestamp
      ? moment(new Date(currentMessage.timestamp * 1000)).format('DD MMM YYYY')
      : moment(currentMessage.createdAt).format('DD MMM YYYY');

    const previousDate = previousMessage.timestamp
      ? moment(new Date(previousMessage.timestamp * 1000)).format('DD MMM YYYY')
      : moment(previousMessage.createdAt).format('DD MMM YYYY');

    return currentDate !== previousDate;
  }

  scrollToBottom(): void {
    setTimeout(() => {
      if (this.messagesContainer) {
        const element = this.messagesContainer.nativeElement;
        element.scrollTop = element.scrollHeight;
      }
    }, 100);
  }

  onScroll(event: Event): void {
    const element = event.target as HTMLElement;
    if (!element) return;

    // Check if scrolled to top (within 100px threshold)
    const scrollTop = element.scrollTop;
    const threshold = 100;

    if (scrollTop <= threshold && !this.loadingMore && this.hasMoreMessages && this.patient?.id) {
      this.loadMoreMessages();
    }
  }

  loadMoreMessages(): void {
    if (this.loadingMore || !this.hasMoreMessages || !this.patient?.id) {
      return;
    }

    this.loadingMore = true;
    const previousScrollHeight = this.messagesContainer?.nativeElement?.scrollHeight || 0;
    const previousScrollTop = this.messagesContainer?.nativeElement?.scrollTop || 0;

    // Calculate new offset
    const newOffset = this.messagesOffset + this.messagesLimit;

    this.whatsappService.loadMessagesByPatient(this.patient.id, this.messagesLimit, newOffset)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.loadingMore = false;

          if (response.success && response.data && Array.isArray(response.data)) {
            const newMessages = response.data;
            const totalCount = response.total || 0;

            if (newMessages.length === 0) {
              // No more messages to load
              this.hasMoreMessages = false;
              return;
            }

            // Filter messages for the selected conversation
            const conversationMessages = newMessages
              .filter((msg: any) => {
                if (!this.selectedConversation) return false;
                const phoneNumber = msg.direction === 'inbound' ? msg.from : msg.to;
                return phoneNumber === this.selectedConversation.phoneNumber;
              })
              .sort((a: any, b: any) => {
                // Sort messages chronologically by createdAt (oldest first)
                const timeA = new Date(a.createdAt).getTime();
                const timeB = new Date(b.createdAt).getTime();
                return timeA - timeB; // Oldest first
              });

            if (conversationMessages.length > 0) {
              // Enhance new messages with computed properties
              this.enhanceMessages(conversationMessages);

              // Prepend new messages to the beginning of the messages array
              const oldMessages = [...this.messages];
              this.messages = [...conversationMessages, ...oldMessages];

              // Update offset
              this.messagesOffset = newOffset;

              // Check if there are more messages to load
              this.hasMoreMessages = newMessages.length === this.messagesLimit &&
                (this.messagesOffset + this.messagesLimit) < totalCount;

              // Maintain scroll position after loading new messages
              setTimeout(() => {
                if (this.messagesContainer) {
                  const element = this.messagesContainer.nativeElement;
                  const newScrollHeight = element.scrollHeight;
                  const scrollDifference = newScrollHeight - previousScrollHeight;
                  element.scrollTop = previousScrollTop + scrollDifference;
                }
              }, 50);
            } else {
              // No messages for this conversation in this batch, but might be more overall
              this.messagesOffset = newOffset;
              this.hasMoreMessages = newMessages.length === this.messagesLimit &&
                (this.messagesOffset + this.messagesLimit) < totalCount;

              // If we got messages but none for this conversation, try loading more
              if (this.hasMoreMessages) {
                // Recursively try to load more (with a limit to prevent infinite loops)
                setTimeout(() => {
                  if (this.messagesContainer?.nativeElement?.scrollTop <= 100) {
                    this.loadMoreMessages();
                  }
                }, 100);
              }
            }
          } else {
            this.hasMoreMessages = false;
          }
        },
        error: (error) => {
          this.loadingMore = false;
          console.error('[DIALOG] Error loading more messages:', error);
          this.hasMoreMessages = false;
        }
      });
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (this.hasTextOrMedia()) {
        this.sendMessageWithMedia();
      }
    }
  }

  hasTextOrMedia(): boolean {
    return (this.newMessage && this.newMessage.trim().length > 0) || this.attachedMedia.length > 0;
  }

  hasText(): boolean {
    return !!(this.newMessage && this.newMessage.trim().length > 0);
  }

  private updateMessagePlaceholder(): void {
    if (this.hasInsufficientCredits) {
      this.messagePlaceholder = 'Insufficient credits to send messages. Please recharge.';
      return;
    }

    if (this.pendingRecording) {
      this.messagePlaceholder = 'Click send to send voice note or delete to discard';
      return;
    }

    if (this.selectedConversation) {
      this.messagePlaceholder = 'Type a message...';
      return;
    }

    if (this.patient?.mobileNumber) {
      this.messagePlaceholder = 'Type a message to ' + this.formatPhoneNumber(this.patient.mobileNumber) + '...';
      return;
    }

    this.messagePlaceholder = 'Select a conversation or ensure patient has a mobile number';
  }

  onSendOrRecordClick(event: MouseEvent): void {
    // Prevent click if touch event just occurred (to avoid double-triggering)
    if (this.touchEventJustOccurred) {
      this.touchEventJustOccurred = false;
      return;
    }

    // If we're using touch interaction, ignore click events
    if (this.isUsingTouchInteraction) {
      return;
    }

    // Mark that we're using click interaction (for hybrid devices)
    this.isUsingTouchInteraction = false;

    if (this.hasText()) {
      // If there's text, show send menu (can send as text or convert to PDF)
      this.toggleSendMenu();
    } else if (this.attachedMedia.length > 0) {
      // If there's only media (no text), send directly
      this.sendMessageWithMedia();
    } else {
      // No text or media, toggle recording (works on all devices)
      this.toggleRecording();
    }
  }

  toggleSendMenu(): void {
    this.sendMenuOpen = !this.sendMenuOpen;
    if (this.sendMenuOpen) {
      this.closeAttachmentMenu(); // Close attachment menu if open
    }
  }

  closeSendMenu(): void {
    this.sendMenuOpen = false;
  }

  sendAsText(): void {
    this.closeSendMenu();
    this.sendMessageWithMedia();
  }

  async sendAsPdf(): Promise<void> {
    this.closeSendMenu();
    this.includeSignatureInPdf = false;
    await this.sendMessageAsPdf();
  }

  async sendAsPdfWithSignature(): Promise<void> {
    this.closeSendMenu();
    this.includeSignatureInPdf = true;
    await this.sendMessageAsPdf();
  }

  async sendMessageAsPdf(): Promise<void> {
    if (!this.newMessage.trim()) {
      return;
    }

    // Handle conversation selection for single sends
    if (!this.broadcastMode) {
      let phoneNumber = this.selectedConversation?.phoneNumber;

      if (!phoneNumber && this.patient?.mobileNumber) {
        phoneNumber = this.patient.mobileNumber;

        if (!this.selectedConversation) {
          const conversations = this.whatsappService.getCurrentConversations() || [];
          let conversation = conversations.find(c => c.phoneNumber === phoneNumber);
          if (!conversation) {
            conversation = {
              phoneNumber,
              messages: [],
              lastMessageAt: null,
              unreadCount: 0,
              hasPendingMessage: false
            };
          }
          this.selectConversation(conversation);
        }
      }
    }

    const messageToSend = this.newMessage.trim();

    try {
      // Get signature details if needed
      const signatureDetails = this.includeSignatureInPdf
        ? this.signatureService.getSignatureDetails(true, this.selectedDoctor)
        : {};

      // Build images array from attached media for PDF embedding (same as timeline email/PDF)
      const pdfImages = this.getAttachedMediaForPdf();

      // Generate PDF (shared for broadcast, single for single send)
      const pdfDataUrl = await this.pdfDownloadService.generatePdfAsBase64({
        content: messageToSend,
        filename: `Message-${this.patient.name}-${this.patient.surname}-${Date.now()}`,
        patient: this.patient,
        images: pdfImages.length > 0 ? pdfImages : undefined,
        attachSignature: this.includeSignatureInPdf && !!signatureDetails.signatureUrl,
        signatureUrl: signatureDetails.signatureUrl,
        signatoryName: signatureDetails.signatureName,
      });

      const fileName = `Message-${this.patient.name}-${this.patient.surname}-${Date.now()}.pdf`;

      await this.sendUnified(
        (phoneNumber, options) => {
          return this.whatsappService.sendMedia(
            phoneNumber,
            pdfDataUrl,
            'application/pdf',
            fileName,
            'document',
            undefined,
            options
          );
        },
        'PDF',
        true,
        (response) => {

          this.scrollToBottom();

          // Clear attached media and message after PDF was sent (content is in the PDF)
          this.attachedMedia = [];
          this.newMessage = '';
          this.autoResizeTextarea();

          if (response.waitingForButtonResponse) {
            this.showToastMessage('Template message sent. Waiting for user response...');
          } else if (response.introTemplateSent) {
            this.showToastMessage('Intro template and PDF sent successfully');
          } else {
            this.showToastMessage('PDF sent successfully');
          }
        },
        (error) => {
          this.showToastMessage('Failed to send PDF: ' + (error?.error || error?.message || 'Unknown error'));
        },
        messageToSend
      );
    } catch (error: any) {
      console.error('[DIALOG] Error generating PDF', error);
      this.showToastMessage('Error generating PDF: ' + (error.message || 'Unknown error'));
    }
  }

  // Mouse down/up/leave handlers not needed for desktop - we use click to toggle

  onSendOrRecordTouchStart(event: TouchEvent): void {
    if (!this.hasTextOrMedia()) {
      this.touchEventJustOccurred = true;
      // Clear the flag after a short delay to allow click events again
      setTimeout(() => {
        this.touchEventJustOccurred = false;
      }, 300);
      this.onTouchStart(event);
    }
  }

  onSendOrRecordTouchEnd(event: TouchEvent): void {
    if (!this.hasTextOrMedia()) {
      this.touchEventJustOccurred = true;
      // Clear the flag after a short delay to allow click events again
      setTimeout(() => {
        this.touchEventJustOccurred = false;
      }, 300);
      this.onTouchEnd(event);
    }
  }

  onSendOrRecordTouchMove(event: TouchEvent): void {
    if (!this.hasTextOrMedia()) {
      this.onTouchMove(event);
    }
  }

  onSendOrRecordTouchCancel(event: TouchEvent): void {
    // Handle touch cancel (e.g., touch moves outside element)
    if (this.isUsingTouchInteraction && this.recording) {
      // Cancel the recording if touch is cancelled
      this.cancelRecording();
      this.isUsingTouchInteraction = false;
    }
  }

  close(): void {
    // Clean up recording if active
    this.stopRecordingTimer();
    if (this.recording) {
      this.recordingService.cleanup();
    }
    // Clear pending recording
    this.pendingRecording = null;
    this.dialogRef.close();
  }

  /**
   * Toggle recording (works for both click and touch devices)
   * On touch devices, this is used for click events (click to start, click to stop)
   * Touch events use hold-to-record via onTouchStart/onTouchEnd
   */
  toggleRecording(): void {
    if (this.recording) {
      this.stopRecording();
    } else {
      this.startRecording();
    }
  }

  // Mouse down/up/leave handlers removed - desktop uses click to toggle recording
  // Touch devices use touch start/end for hold-to-record

  /**
   * Handle touch start (for hold-to-record on touch devices)
   * Works on both mobile and touchscreen laptops
   */
  onTouchStart(event: TouchEvent): void {
    // Mark that we're using touch interaction
    this.isUsingTouchInteraction = true;
    event.preventDefault();
    event.stopPropagation(); // Prevent click event from firing

    // Record initial touch position
    if (event.touches && event.touches.length > 0) {
      this.touchStartX = event.touches[0].clientX;
      this.touchStartY = event.touches[0].clientY;
      this.touchCurrentX = this.touchStartX;
      this.touchCurrentY = this.touchStartY;
      this.showCancelHint = false;
      this.showLockHint = false;
    }

    if (!this.recording) {
      this.startRecording();
    }
  }

  /**
   * Handle touch move (for slide gestures during recording)
   */
  onTouchMove(event: TouchEvent): void {
    if (!this.recording || !this.isUsingTouchInteraction) {
      return;
    }

    if (event.touches && event.touches.length > 0) {
      this.touchCurrentX = event.touches[0].clientX;
      this.touchCurrentY = event.touches[0].clientY;

      const deltaX = this.touchCurrentX - this.touchStartX;
      const deltaY = this.touchStartY - this.touchCurrentY; // Inverted because slide up means Y decreases

      // Show hints based on gesture direction
      // Slide left (negative deltaX) = cancel
      // Slide up (positive deltaY) = lock recording (continue without holding)
      const slideThreshold = 50; // pixels

      if (deltaX < -slideThreshold && Math.abs(deltaY) < Math.abs(deltaX) / 2) {
        // Slide left (cancel)
        this.showCancelHint = true;
        this.showLockHint = false;
      } else if (deltaY > slideThreshold && Math.abs(deltaX) < Math.abs(deltaY) / 2) {
        // Slide up (lock recording - continue without holding)
        this.showLockHint = true;
        this.showCancelHint = false;
      } else {
        // Reset hints if not sliding in a clear direction
        this.showCancelHint = false;
        this.showLockHint = false;
      }

      this.cdr.markForCheck();
    }
  }

  /**
   * Handle touch end (for hold-to-record)
   * Works on both mobile and touchscreen laptops
   */
  onTouchEnd(event: TouchEvent): void {
    if (!this.isUsingTouchInteraction) {
      return;
    }

    if (!this.recording) {
      this.isUsingTouchInteraction = false;
      return;
    }

    // Check if user slid left (cancel) or up (lock)
    const deltaX = this.touchCurrentX - this.touchStartX;
    const deltaY = this.touchStartY - this.touchCurrentY; // Inverted because slide up means Y decreases
    const slideThreshold = 50; // pixels

    if (deltaX < -slideThreshold && Math.abs(deltaY) < Math.abs(deltaX) / 2) {
      // Slide left = cancel recording
      this.cancelRecording();
      this.showCancelHint = false;
      this.showLockHint = false;
      this.isUsingTouchInteraction = false;
      event.preventDefault();
      event.stopPropagation();
      return;
    } else if (deltaY > slideThreshold && Math.abs(deltaX) < Math.abs(deltaY) / 2) {
      // Slide up = lock recording (continue recording, switch to click controls)
      this.lockRecording();
      this.showCancelHint = false;
      this.showLockHint = false;
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    // Normal release = stop recording (will show send/delete buttons)
    event.preventDefault();
    event.stopPropagation(); // Prevent click event from firing
    this.stopRecording();
    this.showCancelHint = false;
    this.showLockHint = false;
    this.isUsingTouchInteraction = false;
  }

  /**
   * (Re)register this dialog's recording configuration on the shared RecordingService.
   *
   * RecordingService is a root singleton shared with the AI dialog, and cleanup() resets
   * its mode and callbacks. So this must run before every startRecording(), not once in
   * ngOnInit, and it must set mode explicitly so a previous consumer's 'segmented' mode
   * can never route our final blob to a stale onSegment callback.
   */
  private configureRecordingService(): void {
    this.recordingService.configure({
      mode: 'single',
      onSegment: undefined,
      onBlobReady: (recording) => {
        // Don't store recording if we're canceling
        if (this.isCancelingRecording) {
          this.isCancelingRecording = false;
          return;
        }
        // If shouldSendDirectly is set (click-to-record mode), send immediately
        if (this.shouldSendDirectly) {
          this.shouldSendDirectly = false;
          this.recording = false;
          this.stopRecordingTimer();
          this.updateMessagePlaceholder();
          this.cdr.markForCheck();
          // Send the recording directly (cleanup will happen in sendVoiceNote callback)
          this.sendVoiceNote(recording.base64, recording.mimeType);
          return;
        }
        // Store recording instead of auto-sending - user must click send button
        this.pendingRecording = recording;
        this.recording = false;
        this.updateMessagePlaceholder();
        this.cdr.markForCheck();
      },
      onError: (error) => {
        this.showToastMessage(error);
      },
      onStateChange: (state) => {
        const wasRecording = this.recording;
        this.recording = state.isRecording;

        if (this.recording && !wasRecording) {
          // Recording just started - initialize timer
          this.recordingTime = new Date(0);
          this.startRecordingTimer();
        } else if (!this.recording && wasRecording) {
          // Recording just stopped - stop timer
          this.stopRecordingTimer();
        } else if (this.recording && !this.recordingPaused) {
          // Update time from service if not paused (backup, but our timer is primary)
          // Only sync if there's a significant difference (service might be slightly ahead)
          const serviceTime = state.recordingTime.getTime();
          const ourTime = this.recordingTime.getTime();
          if (Math.abs(serviceTime - ourTime) > 2000) {
            this.recordingTime = state.recordingTime;
          }
        }
        this.cdr.markForCheck();
      }
    });
  }

  async startRecording(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (this.recording || (!this.selectedConversation && !this.patient?.mobileNumber)) {
      return;
    }

    // Reset state
    this.recordingPaused = false;
    this.pendingRecording = null;
    this.showCancelHint = false;
    this.showLockHint = false;
    this.isCancelingRecording = false;
    this.shouldSendDirectly = false;
    this.recordingTime = new Date(0);
    this.pausedRecordingTime = new Date(0);
    // Don't reset isUsingTouchInteraction here - it will be set by the interaction method

    this.configureRecordingService();
    const success = await this.recordingService.startRecording();
    if (!success) {
      // Error message is handled by service callback
      return;
    }

    // Keep screen awake while recording
    this.screenWake.disableScreenSleep();

    // Start our timer
    this.startRecordingTimer();
  }

  async stopRecording(): Promise<void> {
    this.stopRecordingTimer();
    await this.recordingService.stopRecording();
    // Recording will be stored in pendingRecording via callback
    // Re-enable screen sleep
    this.screenWake.enableScreenSleep();
  }

  async stopAndSendRecording(): Promise<void> {
    // Set flag to send directly when blob is ready (for click-to-record mode)
    this.shouldSendDirectly = true;
    this.stopRecordingTimer();
    await this.recordingService.stopRecording();
    // Recording will be sent directly via callback
    // Re-enable screen sleep
    this.screenWake.enableScreenSleep();
  }

  lockRecording(): void {
    // Lock recording - switch from touch interaction to click controls
    // Recording continues, but user can now use pause/delete/send buttons
    this.isUsingTouchInteraction = false;
    this.showCancelHint = false;
    this.showLockHint = false;
    this.cdr.markForCheck();
  }

  cancelRecording(): void {
    // Cancel current recording
    this.isCancelingRecording = true; // Set flag to prevent callback from setting pendingRecording
    this.shouldSendDirectly = false; // Reset send directly flag
    this.stopRecordingTimer();
    if (this.recording) {
      this.recordingService.cleanup();
      this.recording = false;
      this.recordingTime = new Date(0);
      this.recordingPaused = false;
      this.pausedRecordingTime = new Date(0);
    }
    this.pendingRecording = null;
    this.showCancelHint = false;
    this.showLockHint = false;
    this.updateMessagePlaceholder();
    // Re-enable screen sleep
    this.screenWake.enableScreenSleep();
    // Reset flag after a short delay in case cleanup triggers callback
    setTimeout(() => {
      this.isCancelingRecording = false;
    }, 100);
    this.cdr.markForCheck();
  }

  deleteRecording(): void {
    // Delete pending recording and reset all state
    this.pendingRecording = null;

    // Reset cancel flag immediately to allow new recordings
    this.isCancelingRecording = false;
    this.shouldSendDirectly = false;

    // Clean up any active recording state if it exists
    if (this.recording) {
      this.stopRecordingTimer();
      this.recordingService.cleanup();
      this.recording = false;
    } else {
      // Even if not actively recording, ensure service is clean
      // This is important for pending recordings that were stopped
      this.recordingService.cleanup();
    }

    // Re-enable screen sleep
    this.screenWake.enableScreenSleep();

    // Reset all recording state
    this.recordingTime = new Date(0);
    this.recordingPaused = false;
    this.pausedRecordingTime = new Date(0);

    // Reset interaction state
    this.isUsingTouchInteraction = false;
    this.showCancelHint = false;
    this.showLockHint = false;

    // Reset touch event flag to ensure touch events work again
    this.touchEventJustOccurred = false;

    // Reset touch positions
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.touchCurrentX = 0;
    this.touchCurrentY = 0;

    this.updateMessagePlaceholder();
    this.cdr.markForCheck();
  }

  pauseRecording(): void {
    if (!this.recording) {
      return;
    }

    this.recordingPaused = !this.recordingPaused;

    if (this.recordingPaused) {
      // Store the current time when pausing
      this.pausedRecordingTime = new Date(this.recordingTime);
      // Stop the timer updates
      this.stopRecordingTimer();
    } else {
      // Resume - restart timer from paused time
      this.startRecordingTimer();
    }

    this.cdr.markForCheck();
  }

  private startRecordingTimer(): void {
    // Clear any existing timer
    this.stopRecordingTimer();

    if (!this.recording || this.recordingPaused) {
      return;
    }

    // Start timer that updates every second
    this.recordingTimerInterval = setInterval(() => {
      if (this.recording && !this.recordingPaused) {
        // Increment time by 1 second
        this.recordingTime = new Date(this.recordingTime.getTime() + 1000);
        this.cdr.markForCheck();
      }
    }, 1000);
  }

  private stopRecordingTimer(): void {
    if (this.recordingTimerInterval) {
      clearInterval(this.recordingTimerInterval);
      this.recordingTimerInterval = null;
    }
  }

  sendPendingRecording(): void {
    if (!this.pendingRecording) {
      return;
    }

    const recording = this.pendingRecording;

    // Set cancel flag to prevent callback from setting pendingRecording again
    this.isCancelingRecording = true;
    this.shouldSendDirectly = false;

    // Reset all recording state immediately
    this.pendingRecording = null;
    this.recording = false;
    this.recordingPaused = false;
    this.recordingTime = new Date(0);
    this.pausedRecordingTime = new Date(0);
    this.isUsingTouchInteraction = false;
    this.showCancelHint = false;
    this.showLockHint = false;
    this.stopRecordingTimer();
    // Re-enable screen sleep
    this.screenWake.enableScreenSleep();

    // Clean up recording service to prevent any callbacks
    this.recordingService.cleanup();

    // Reset cancel flag after a short delay
    setTimeout(() => {
      this.isCancelingRecording = false;
    }, 100);

    this.updateMessagePlaceholder();
    this.cdr.markForCheck();

    // Send the recording
    this.sendVoiceNote(recording.base64, recording.mimeType);
  }

  sendVoiceNote(audioBase64: string, mimeType: string): void {

    this.sendUnified(
      (phoneNumber, options) => this.whatsappService.sendVoiceNote(phoneNumber, audioBase64, mimeType, options),
      'voice note',
      false,
      (response) => {
        if (response?.status === 'pending_button_response') {
          this.showToastMessage('Voice note queued — waiting for recipient to confirm consent');
        }
        this.scrollToBottom();
        this.cleanupRecorder();
      },
      () => {
        this.cleanupRecorder();
      },
      undefined,
      { messageType: 'voice', mediaType: 'audio/ogg' }
    );
  }

  private cleanupRecorder(): void {
    this.recordingService.cleanup();
  }

  // ==================== PENDING MESSAGE HELPERS ====================

  /**
   * Whether a chat row is a pending (queued-behind-template) entry.
   * Used by the template to switch between the standard message bubble
   * and the pending-state bubble.
   */
  isPending(message: any): boolean {
    return !!message && message.__kind === 'pending';
  }

  /**
   * Human-readable label for a pending row's current status. Used in the
   * pending bubble's footer / banner.
   */
  pendingStatusLabel(message: any): string {
    switch (message?.status) {
      case 'pending': return 'Waiting for recipient to confirm consent';
      case 'processing': return 'Sending now…';
      case 'expired': return "Recipient didn't respond within 1 hour";
      case 'rejected': return 'Recipient declined the consent prompt';
      default: return 'Queued';
    }
  }

  /**
   * Tooltip text for the red "failed" icon. Surfaces Meta's structured error
   * (errorCode + errorMessage populated by processStatusUpdate on the backend)
   * so the user can see why delivery failed instead of a generic "Failed to
   * send". Falls back to the generic label when the columns aren't populated.
   */
  failedTooltip(message: any): string {
    const code = message?.errorCode;
    const hint = message?.errorMessage;
    if (code && hint) return `Failed (${code}): ${hint}`;
    if (code) return `Failed (${code})`;
    if (hint) return `Failed: ${hint}`;
    return 'Failed to send';
  }

  /**
   * Material icon name to render inside the pending bubble.
   */
  pendingStatusIcon(message: any): string {
    switch (message?.status) {
      case 'pending': return 'schedule';
      case 'processing': return 'sync';
      case 'expired': return 'schedule_send';
      case 'rejected': return 'cancel';
      default: return 'schedule';
    }
  }

  /**
   * Re-send the consent template for a queued message. The original payload
   * (text / voice / media) stays in place server-side; only the consent
   * template rotates and `expiresAt` resets.
   */
  retryPendingMessage(message: any): void {
    if (!message || !message.id) return;
    this.whatsappService.retryPendingMessage(message.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe(response => {
        if (response?.success) {
          this.showToastMessage('Consent prompt re-sent to recipient');
        } else {
          this.showToastMessage(response?.error || 'Failed to retry pending message');
        }
      });
  }

  /**
   * Cancel a queued pending message. Confirms first, then deletes the row
   * and the associated blob server-side. The chat row vanishes via the
   * `whatsappPendingDeleted` socket event.
   */
  async cancelPendingMessage(message: any): Promise<void> {
    if (!message || !message.id) return;
    const confirmed = await this.feedbackService.showConfirmation(
      'Cancel queued message?',
      'This message will be removed and not sent to the recipient.',
      undefined,
      'Keep',
      'Cancel message',
      false
    );
    if (!confirmed) return;
    this.whatsappService.cancelPendingMessage(message.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe(response => {
        if (response?.success) {
          this.showToastMessage('Queued message cancelled');
        } else {
          this.showToastMessage(response?.error || 'Failed to cancel pending message');
        }
      });
  }

  /**
   * Media attachment methods
   */
  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;

  toggleAttachmentMenu(): void {
    this.attachmentMenuOpen = !this.attachmentMenuOpen;
  }

  closeAttachmentMenu(): void {
    this.attachmentMenuOpen = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;

    // Close attachment menu if clicking outside
    if (this.attachmentMenuOpen) {
      const menuContainer = target.closest('.attachment-menu-container');
      if (!menuContainer) {
        this.closeAttachmentMenu();
      }
    }

    // Close send menu if clicking outside
    if (this.sendMenuOpen) {
      const sendMenuContainer = target.closest('.send-menu-container');
      const sendButton = target.closest('.send-button, .voice-button');
      if (!sendMenuContainer && !sendButton) {
        this.closeSendMenu();
      }
    }
  }

  triggerFileInput(): void {
    this.closeAttachmentMenu();
    this.fileInput?.nativeElement.click();
  }

  onFileSelected(event: any): void {
    const files = Array.from(event.target.files) as File[];
    this.handleFiles(files);
    // Reset file input
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    const files = Array.from(event.dataTransfer?.files || []) as File[];
    this.handleFiles(files);
  }

  handleFiles(files: File[]): void {
    if (!files || files.length === 0) {
      return;
    }

    files.forEach(file => {
      // Determine media type - try MIME type first, then fallback to file extension
      let mediaType = this.getMediaTypeFromMimeType(file.type);
      
      // If MIME type detection failed, try to detect from file extension
      if (!mediaType) {
        mediaType = this.getMediaTypeFromFileName(file.name);
      }
      
      // If still no type detected, try to detect from file content
      if (!mediaType) {
        const reader = new FileReader();
        reader.onload = (e: any) => {
          const result = e.target.result;
          // Try to extract MIME type from data URL
          const mimeMatch = result.match(/^data:([^;]+);/);
          if (mimeMatch) {
            const detectedMimeType = mimeMatch[1];
            mediaType = this.getMediaTypeFromMimeType(detectedMimeType);
            
            if (mediaType) {
              const mediaItem = {
                file,
                mediaUrl: result,
                mimeType: detectedMimeType,
                filename: file.name || (mediaType === 'image' ? 'image.png' : mediaType === 'video' ? 'video.mp4' : 'file'),
                mediaType,
                preview: result
              };
              this.attachedMedia.push(mediaItem);
              return;
            }
          }
          
          // If data URL doesn't have MIME type, try to detect from file content (magic bytes)
          // Check for common image formats by examining the base64 content
          const base64Data = result.split(',')[1];
          if (base64Data) {
            // Decode first few bytes to check magic numbers
            try {
              const binaryString = atob(base64Data.substring(0, 20));
              const bytes = new Uint8Array(binaryString.length);
              for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
              }
              
              // Check for image magic bytes
              // PNG: 89 50 4E 47
              // JPEG: FF D8 FF
              // GIF: 47 49 46 38
              // WebP: RIFF...WEBP
              if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4E && bytes[3] === 0x47) {
                // PNG
                mediaType = 'image';
                const mediaItem = {
                  file,
                  mediaUrl: result,
                  mimeType: 'image/png',
                  filename: file.name || 'image.png',
                  mediaType,
                  preview: result
                };
                this.attachedMedia.push(mediaItem);
                return;
              } else if (bytes[0] === 0xFF && bytes[1] === 0xD8 && bytes[2] === 0xFF) {
                // JPEG
                mediaType = 'image';
                const mediaItem = {
                  file,
                  mediaUrl: result,
                  mimeType: 'image/jpeg',
                  filename: file.name || 'image.jpg',
                  mediaType,
                  preview: result
                };
                this.attachedMedia.push(mediaItem);
                return;
              } else if (bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x38) {
                // GIF
                mediaType = 'image';
                const mediaItem = {
                  file,
                  mediaUrl: result,
                  mimeType: 'image/gif',
                  filename: file.name || 'image.gif',
                  mediaType,
                  preview: result
                };
                this.attachedMedia.push(mediaItem);
                return;
              }
            } catch (err) {
              // If detection fails, continue to error message
            }
          }
          
          // If we still can't determine type, show error
          this.showToastMessage(`File type not supported: ${file.name || 'unknown'}`);
        };
        reader.onerror = () => {
          this.showToastMessage(`Failed to read file: ${file.name}`);
        };
        reader.readAsDataURL(file);
        return; // Exit early, will continue in reader.onload
      }

      // If we have a valid media type, proceed normally
      // Read file as base64 or URL
      const reader = new FileReader();
      reader.onload = (e: any) => {
        const result = e.target.result;
        const mediaItem = {
          file,
          mediaUrl: result, // Base64 data URL or URL
          mimeType: file.type || this.getMimeTypeFromFileName(file.name) || 'image/png',
          filename: file.name || (mediaType === 'image' ? 'image.png' : 'file'),
          mediaType,
          preview: result // For preview
        };
        this.attachedMedia.push(mediaItem);
      };
      reader.onerror = () => {
        this.showToastMessage(`Failed to read file: ${file.name}`);
      };
      reader.readAsDataURL(file);
    });
  }

  getMediaTypeFromMimeType(mimeType: string): 'image' | 'document' | 'video' | null {
    if (!mimeType) {
      return null;
    }
    
    if (mimeType.startsWith('image/')) {
      return 'image';
    } else if (mimeType.startsWith('video/')) {
      return 'video';
    } else if (mimeType.startsWith('application/') || mimeType.startsWith('text/')) {
      return 'document';
    }
    return null;
  }

  /**
   * Build images array from attached media for PDF embedding (same shape as timeline email/PDF).
   * Only includes image MIME types with http(s) URLs so the PDF service's image proxy can fetch them.
   */
  private getAttachedMediaForPdf(): { mediaUrl: string; displayIndex: number; mediaType: string }[] {
    if (!this.attachedMedia?.length) {
      return [];
    }
    const mimeToFormat = (mime: string): string => {
      if (!mime || !mime.startsWith('image/')) {
        return 'png';
      }
      const part = mime.split('/')[1]?.toLowerCase();
      return part === 'jpg' ? 'jpeg' : (part || 'png');
    };
    return this.attachedMedia
      .filter((m: any) => {
        const mime = m.mimeType || m.mediaType;
        const url = m.mediaUrl || m.preview;
        return mime?.startsWith('image/') && url && (url.startsWith('http://') || url.startsWith('https://'));
      })
      .map((m: any, i: number) => ({
        mediaUrl: m.mediaUrl || m.preview,
        displayIndex: m.displayIndex ?? i,
        mediaType: mimeToFormat(m.mimeType || m.mediaType)
      }));
  }

  /**
   * Detect media type from file name/extension as fallback
   */
  getMediaTypeFromFileName(filename: string): 'image' | 'document' | 'video' | null {
    if (!filename) {
      return null;
    }
    
    const extension = filename.toLowerCase().split('.').pop() || '';
    
    // Image extensions
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'ico'];
    if (imageExtensions.includes(extension)) {
      return 'image';
    }
    
    // Video extensions
    const videoExtensions = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv', 'm4v'];
    if (videoExtensions.includes(extension)) {
      return 'video';
    }
    
    // Document extensions (PDF, Office docs, etc.)
    const documentExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'rtf'];
    if (documentExtensions.includes(extension)) {
      return 'document';
    }
    
    return null;
  }

  /**
   * Get MIME type from file name/extension
   */
  getMimeTypeFromFileName(filename: string): string | null {
    if (!filename) {
      return null;
    }
    
    const extension = filename.toLowerCase().split('.').pop() || '';
    
    const mimeTypes: { [key: string]: string } = {
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'png': 'image/png',
      'gif': 'image/gif',
      'bmp': 'image/bmp',
      'webp': 'image/webp',
      'svg': 'image/svg+xml',
      'ico': 'image/x-icon',
      'mp4': 'video/mp4',
      'avi': 'video/x-msvideo',
      'mov': 'video/quicktime',
      'webm': 'video/webm',
      'pdf': 'application/pdf',
      'doc': 'application/msword',
      'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'xls': 'application/vnd.ms-excel',
      'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'txt': 'text/plain'
    };
    
    return mimeTypes[extension] || null;
  }

  removeAttachedMedia(index: number): void {
    this.attachedMedia.splice(index, 1);
  }

  openMediaSelector(): void {
    this.closeAttachmentMenu();
    if (!this.patient || this.patientMedia.length === 0) {
      this.showToastMessage('No media files available for this patient.');
      return;
    }

    // Create a simple dialog to select media using file-gallery
    const mediaDialog = this.dialog.open(MediaSelectorDialogComponent, {
      width: '800px',
      maxWidth: '90vw',
      maxHeight: '90vh',
      data: {
        patientMedia: this.patientMedia
      }
    });

    mediaDialog.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((selectedMediaArray: any[] | any) => {
      if (selectedMediaArray) {
        // Handle both single item (backward compatibility) and array
        const mediaArray = Array.isArray(selectedMediaArray) ? selectedMediaArray : [selectedMediaArray];

        mediaArray.forEach((selectedMedia: any) => {
          const mediaItem = {
            mediaUrl: selectedMedia.mediaUrl,
            mimeType: selectedMedia.mediaType,
            filename: selectedMedia.mediaFileName || 'media',
            mediaType: this.getMediaTypeFromMimeType(selectedMedia.mediaType),
            preview: selectedMedia.mediaUrl,
            isExisting: true
          };
          if (mediaItem.mediaType) {
            this.attachedMedia.push(mediaItem);
          }
        });
      }
    });
  }

  sendMedia(mediaItem: any, caption?: string): void {
    // Prepare media data
    const finalCaption = caption || this.newMessage.trim() || undefined;
    const messageToClear = this.newMessage.trim();

    // Send using unified method
    this.sendUnified(
      (phoneNumber, options) => {
        return this.whatsappService.sendMedia(
          phoneNumber,
          mediaItem.mediaUrl,
          mediaItem.mimeType,
          mediaItem.filename,
          mediaItem.mediaType,
          finalCaption,
          options
        );
      },
      mediaItem.mediaType,
      false,
      (response) => {
        // Remove from attached media
        const index = this.attachedMedia.indexOf(mediaItem);
        if (index >= 0) {
          this.attachedMedia.splice(index, 1);
        }
        // Clear message if it was used as caption
        if (finalCaption === messageToClear) {
          this.newMessage = '';
          this.autoResizeTextarea();
        }
        this.scrollToBottom();

        if (response.waitingForButtonResponse) {
          this.showToastMessage('Template message sent. Waiting for user response...');
        }
      }
    );
  }

  async sendMessageWithMedia(): Promise<void> {
    // If there are attached media, send them first, then send text message
    if (this.attachedMedia.length > 0) {
      const textMessage = this.newMessage.trim();
      const hasText = textMessage.length > 0;
      
      // Check if first media item is a document (which doesn't support captions)
      const firstMediaIsDocument = this.attachedMedia[0]?.mediaType === 'document';
      
      // If first media is a document and we have text, send text first as separate message
      if (firstMediaIsDocument && hasText) {
        // Send text message first (before clearing it)
        try {
          await this.sendMessage();
          // Clear message after successful send
          this.newMessage = '';
          this.autoResizeTextarea();
          
          // After text is sent, send all media items
          this.attachedMedia.forEach((mediaItem) => {
            this.sendMedia(mediaItem, undefined); // No caption for documents
          });
        } catch (error) {
          // If text message fails, keep the message so user can retry
          // Don't restore since sendMessage handles error restoration
          throw error;
        }
        return;
      }
      
      // For non-document media or when no text, use caption on first item
      this.attachedMedia.forEach((mediaItem, index) => {
        // Only add caption to first item if it's not a document
        const caption = index === 0 && hasText && mediaItem.mediaType !== 'document' 
          ? textMessage 
          : undefined;
        this.sendMedia(mediaItem, caption);
      });
      
      // Clear message after sending first media (if used as caption)
      if (hasText && this.attachedMedia.length > 0 && !firstMediaIsDocument) {
        this.newMessage = '';
        this.autoResizeTextarea();
      }
    } else {
      // No media, send text message normally
      await this.sendMessage();
    }
  }

  openPrescriptionSelector(): void {
    this.closeAttachmentMenu();
    if (!this.patient || this.patientPrescriptions.length === 0) {
      this.showToastMessage('No prescriptions available for this patient.');
      return;
    }

    // Import and open prescription selector dialog
    import('../prescription-selector-dialog/prescription-selector-dialog.component').then(module => {
      const PrescriptionSelectorDialogComponent = module.PrescriptionSelectorDialogComponent;

      const prescriptionDialog = this.dialog.open(PrescriptionSelectorDialogComponent, {
        width: '600px',
        maxWidth: '90vw',
        maxHeight: '90vh',
        data: {
          prescriptions: this.patientPrescriptions,
          signatureContext: this.signatureContext,
          includeSignature: this.includeSignatureOnPrescription,
          selectedDoctor: this.selectedDoctor
        }
      });

      prescriptionDialog.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result: any) => {
        if (result) {
          // Handle both single prescription (backward compatibility) and array
          const prescriptions = result.prescriptions || (result.prescription ? [result.prescription] : []);

          if (prescriptions.length > 0 && result.includeSignature !== undefined) {
            // Send each prescription via WhatsApp
            prescriptions.forEach((prescription: any) => {
              this.sendPrescription(prescription, result.includeSignature, result.selectedDoctor);
            });
          }

          // Update signature preferences
          if (result.includeSignature !== undefined) {
            this.includeSignatureOnPrescription = result.includeSignature;
          }
          if (result.selectedDoctor) {
            this.selectedDoctor = result.selectedDoctor;
          }
        }
      });
    });
  }

  async sendPrescription(prescription: any, includeSignature?: boolean, selectedDoctor?: any): Promise<void> {
    if (!this.patient) {
      this.showToastMessage('Patient information is required.');
      return;
    }

    try {
      // Prepare prescription PDF
      const shouldIncludeSignature = includeSignature !== undefined
        ? includeSignature
        : this.signatureService.shouldIncludeSignature(this.includeSignatureOnPrescription);

      const patientName = `${this.patient.name}_${this.patient.surname}`;
      const fileName = `${patientName}_${moment().valueOf()}_prescription.pdf`;

      const pdfBytes = await this.medicationService.createVerifiedPrescriptionPdf(
        prescription,
        shouldIncludeSignature
      );

      // Convert PDF bytes to base64 data URL
      const blob = new Blob([pdfBytes as BlobPart], { type: 'application/pdf' });
      const base64Data = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error('Failed to process prescription PDF'));
        reader.readAsDataURL(blob);
      });

      const caption = this.newMessage.trim() || undefined;

      // Send using unified method
      await this.sendUnified(
        (phoneNumber, options) => {
          return this.whatsappService.sendMedia(
            phoneNumber,
            base64Data,
            'application/pdf',
            fileName,
            'document',
            caption,
            options
          );
        },
        'prescription',
        false,
        (response) => {
          // Clear message if it was used as caption
          if (caption === this.newMessage.trim()) {
            this.newMessage = '';
            this.autoResizeTextarea();
          }
          this.scrollToBottom();

          if (response.waitingForButtonResponse) {
            this.showToastMessage('Template message sent. Waiting for user response...');
          }
        }
      );
    } catch (error: any) {
      console.error('[DIALOG] Error generating prescription PDF:', error);
      this.showToastMessage(error.message || 'Failed to generate prescription PDF.');
    }
  }

  /**
   * §30 — THE PRACTICE'S DOCUMENTS, from the attachment menu beside Prescription and
   * Invoice/Receipt. `choose()` loads, pre-ticks and opens in one call, so this host needs no chip
   * row and no rows of its own — a menu item has nowhere to put a chip row.
   *
   * ── IT SENDS THROUGH THE MANUAL-SEND ENDPOINT, NOT AS RAW MEDIA ──────────────────────────
   *
   * Its siblings push a file at WhatsApp because a prescription PDF is just a file. A practice
   * document is not: a response-required one has to MINT AN INVITE so the patient has somewhere to
   * send it back to, an information-only one rides as an attachment, and either way the send is a
   * row the practice can see in the patient file and an erasure can find. Pushing the blob would
   * deliver the same bytes and record nothing, so the patient's reply would have nowhere to land.
   *
   * `channels: ['whatsapp']` is already an SMS-family channel name in the delivery core
   * (`patientRequestDelivery.js:385`), which routes WhatsApp Cloud first and Twilio second.
   */
  openPracticeDocumentSelector(): void {
    this.closeAttachmentMenu();
    if (!this.patient?.id) {
      this.showToastMessage('Open a patient file to send a practice document.');
      return;
    }

    this.documentAttachmentService.choose({
      context: 'whatsapp',
      groupId: this.patient.groupId,
    }).pipe(takeUntil(this.destroy$)).subscribe((result) => {
      // `undefined` is CANCELLED, and is not an empty selection.
      if (!result || !result.selection.length) return;

      this.patientDocumentRequestService.createRequest({
        patientId: this.patient.id,
        practiceDocumentIds: result.selection,
        channels: ['whatsapp'],
      }).pipe(takeUntil(this.destroy$)).subscribe({
        next: () => this.showToastMessage(result.selection.length === 1
          ? 'Document sent.'
          : `${result.selection.length} documents sent.`),
        // The server's own sentence, which names WHICH document and why — a generic failure here
        // would send the user hunting through settings for a reason the response already gave.
        error: (error) => this.showToastMessage(
          error?.error?.message || 'The document could not be sent. Please try again.'),
      });
    });
  }

  openBillingDocumentSelector(): void {
    this.closeAttachmentMenu();
    if (!this.patient || this.billingDocuments.length === 0) {
      this.showToastMessage('No billing documents available for this patient.');
      return;
    }

    // Import and open billing document selector dialog
    import('../billing-document-selector-dialog/billing-document-selector-dialog.component').then(module => {
      const BillingDocumentSelectorDialogComponent = module.BillingDocumentSelectorDialogComponent;

      const billingDialog = this.dialog.open(BillingDocumentSelectorDialogComponent, {
        width: '600px',
        maxWidth: '90vw',
        maxHeight: '90vh',
        data: {
          billingDocuments: this.billingDocuments
        }
      });

      billingDialog.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((selectedDocuments: BillingDocument | BillingDocument[] | null) => {
        if (selectedDocuments) {
          // Handle both single document (backward compatibility) and array
          const documentsArray = Array.isArray(selectedDocuments) ? selectedDocuments : [selectedDocuments];

          // Send each billing document via WhatsApp
          documentsArray.forEach((document: BillingDocument) => {
            this.sendBillingDocument(document);
          });
        }
      });
    });
  }

  async sendBillingDocument(document: BillingDocument): Promise<void> {
    if (!document.pdfUrl) {
      this.showToastMessage('PDF not available for this document. Please generate the PDF first.');
      return;
    }

    // Prepare document data
    const caption = this.newMessage.trim() || undefined;
    const documentTypeLabel = document.documentType === 'invoice' ? 'Invoice' :
      document.documentType === 'receipt' ? 'Receipt' :
        document.documentType === 'estimate' ? 'Estimate' :
          document.documentType === 'statement' ? 'Statement' : 'Document';
    const fileName = document.pdfFileName || `${documentTypeLabel}-${document.billingNumber}.pdf`;

    // Send using unified method
    await this.sendUnified(
      (phoneNumber, options) => {
        return this.whatsappService.sendMedia(
          phoneNumber,
          document.pdfUrl!, // Azure blob SAS URL
          'application/pdf',
          fileName,
          'document',
          caption,
          options
        );
      },
      documentTypeLabel.toLowerCase(),
      false,
      (response) => {
        // Clear message if it was used as caption
        if (caption === this.newMessage.trim()) {
          this.newMessage = '';
          this.autoResizeTextarea();
        }
        this.scrollToBottom();

        if (response.waitingForButtonResponse) {
          this.showToastMessage('Template message sent. Waiting for user response...');
        }
      }
    );
  }


  // Helper method to check if content is a placeholder/default text
  isPlaceholderContent(content: string | null | undefined): boolean {
    if (!content) {
      return false;
    }
    const trimmedContent = content.trim().toLowerCase();
    const placeholders = [
      '[document message]',
      '[document]',
      '[media]',
      'document message',
      'document',
      'media'
    ];
    return placeholders.includes(trimmedContent);
  }

  // Enhance messages with computed properties to avoid function calls in template
  enhanceMessages(messages: any[]): void {
    messages.forEach(message => {
      // Compute message key
      message._messageKey = this.getMessageKey(message);

      // Compute placeholder check
      message._hasValidCaption = message.content && !this.isPlaceholderContent(message.content);

      // For audio messages, compute audio properties
      if (message.mediaType?.startsWith('audio/')) {
        this.updateAudioProperties(message);
      }
    });
  }

  // Update audio-related computed properties for a message
  updateAudioProperties(message: any): void {
    const messageKey = message._messageKey || this.getMessageKey(message);
    const props = this.audioPlaybackService.getComputedProperties(message);

    message._audioPlaying = props.isPlaying;
    message._audioCurrentTime = props.currentTime;
    message._audioDuration = props.duration;
    message._audioProgress = props.progress;
    message._audioDurationFormatted = props.formattedDuration;
  }

  // Audio playback methods (delegated to service)
  getMessageKey(message: any): string {
    return this.audioPlaybackService.getMessageKey(message);
  }

  getAudioElement(message: any): HTMLAudioElement | null {
    return this.audioPlaybackService.getAudioElement(message);
  }

  onAudioMetadataLoaded(message: any, event: Event): void {
    const audioElement = event.target as HTMLAudioElement;
    const messageKey = this.getMessageKey(message);
    const duration = audioElement.duration || 0;
    const currentTime = audioElement.currentTime || 0;

    this.audioPlaybackService.initializeAudio(messageKey, audioElement);
    this.audioPlaybackService.updateMetadata(messageKey, duration, currentTime);

    // Update computed properties on message
    this.updateAudioProperties(message);

    // Trigger change detection to update duration display
    this.cdr.markForCheck();
  }

  private audioUpdateRafId: number | null = null;

  onAudioTimeUpdate(message: any, event: Event): void {
    const audioElement = event.target as HTMLAudioElement;
    const messageKey = this.getMessageKey(message);
    const currentTime = audioElement.currentTime || 0;
    const duration = audioElement.duration || 0;
    const isPlaying = !audioElement.paused && !audioElement.ended;

    this.audioPlaybackService.updateProgress(messageKey, currentTime, duration, isPlaying);

    // Update computed properties on message
    this.updateAudioProperties(message);

    // Use requestAnimationFrame to throttle change detection for smooth real-time updates
    // This ensures the progress bar and time counter update smoothly without excessive change detection
    if (this.audioUpdateRafId === null) {
      this.audioUpdateRafId = requestAnimationFrame(() => {
        this.cdr.detectChanges();
        this.audioUpdateRafId = null;
      });
    }
  }

  onAudioEnded(message: any): void {
    const messageKey = this.getMessageKey(message);
    this.audioPlaybackService.onAudioEnded(messageKey);

    // Update computed properties on message
    this.updateAudioProperties(message);

    this.cdr.markForCheck();
  }

  onAudioError(message: any, event: Event): void {
    console.error('[WHATSAPP] Audio playback error:', event);
    const messageKey = this.getMessageKey(message);
    this.audioPlaybackService.onAudioError(messageKey);

    // Update computed properties on message
    if (message.mediaType?.startsWith('audio/')) {
      this.updateAudioProperties(message);
    }

    this.cdr.markForCheck();
  }

  isAudioPlaying(message: any): boolean {
    return this.audioPlaybackService.isPlaying(message);
  }

  getAudioProgress(message: any): number {
    return this.audioPlaybackService.getProgress(message);
  }

  formatAudioDuration(message: any): string {
    return this.audioPlaybackService.getFormattedDuration(message);
  }

  toggleAudioPlayback(message: any): void {
    const messageKey = this.getMessageKey(message);

    // Pause all other audio elements first
    this.audioPlaybackService.pauseAllOthers(messageKey);

    // Delegate toggle to service
    this.audioPlaybackService.togglePlayback(message, messageKey);

    // Update computed properties
    this.updateAudioProperties(message);
    this.cdr.markForCheck();
  }

  /**
   * Toggle sidebar visibility
   */
  toggleSidebar(): void {
    this.sidebarExpanded = !this.sidebarExpanded;
  }

  /**
   * Clipboard paste functionality
   */
  setupPasteListener(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.pasteHandler = (event: ClipboardEvent) => {
      // Only handle paste when input is focused
      if (!this.isInputFocused) {
        return;
      }

      // Don't handle if recording or pending recording
      if (this.recording || this.pendingRecording) {
        return;
      }

      const clipboardData = event.clipboardData;
      if (!clipboardData) {
        return;
      }

      // Check for files/images first
      const items = Array.from(clipboardData.items);
      const fileItems = items.filter(item => item.kind === 'file');

      if (fileItems.length > 0) {
        // Handle file paste (images, etc.)
        event.preventDefault();
        event.stopPropagation();

        const files: File[] = [];
        fileItems.forEach(item => {
          const file = item.getAsFile();
          if (file) {
            // If file type is missing or empty, try to infer from clipboard item type
            if (!file.type && item.type) {
              // Create a new File object with the correct type from clipboard item
              const typedFile = new File([file], file.name || 'image.png', { type: item.type });
              files.push(typedFile);
            } else {
              files.push(file);
            }
          }
        });

        if (files.length > 0) {
          this.handleFiles(files);
        }
        return;
      }

      // Check for text
      const text = clipboardData.getData('text/plain');
      if (text && text.trim().length > 0) {
        // Only handle text paste if we're not in a text input that already handles it
        // We'll let the default paste behavior work for text, but we can intercept if needed
        // For now, let text paste work normally (browser default)
        // If we want to customize text paste behavior, we can add it here
      }
    };

    // Add paste listener to document
    document.addEventListener('paste', this.pasteHandler);
  }

  onInputFocus(): void {
    this.isInputFocused = true;
  }

  onInputBlur(): void {
    this.isInputFocused = false;
  }

  override ngOnDestroy(): void {
    // Remove paste listener
    if (this.pasteHandler) {
      document.removeEventListener('paste', this.pasteHandler);
      this.pasteHandler = undefined;
    }

    // Cancel any pending animation frame requests
    if (this.audioUpdateRafId !== null) {
      cancelAnimationFrame(this.audioUpdateRafId);
      this.audioUpdateRafId = null;
    }

    // Cleanup audio playback service state
    this.audioPlaybackService.cleanupAll();

    if (this.recording) {
      this.stopRecording();
    }
    // Cleanup recording service
    this.recordingService.cleanup();
    // Ensure screen sleep is re-enabled on destroy
    this.screenWake.enableScreenSleep();
    super.ngOnDestroy();
  }
}
